import PrinterFeature from './printer-feature';
import PrinterImage from './printer-image';
import PrinterSelect from './printer-select';
import PrinterSpec from './printer-specs';
import {
  getCommonFeatures,
  getFilteredOptions,
  getNextColumn,
  getPrinterFeaturesById,
  getPrinterInfoById,
  MAX_SCREEN_ELEMENTS,
} from './modal-printer-comparison.utils';

import { useEffect, useState } from 'react';
import {
  ProductOptions,
  SelectedOption,
  ModalPrinterComparisonFeatures,
  ProductInfo,
  ModalPrinterComparisonAnalyticsPayload,
} from './types';
import { ContainerColumn, ContainerRows, Feature, Header } from './styles';

const calculateOptions = (
  productsOptions: ProductOptions[],
  selectedArray: string[]
) => {
  const newOptions = selectedArray.map((sku) =>
    getFilteredOptions(productsOptions, sku, selectedArray)
  );
  return newOptions;
};

type ModalPrinterComparisonTableProps = {
  columnsNumber: number;
  initialSelection: string[];
  fullSpecsLabel: string;
  productsInfo: ProductInfo[];
  productsOptions: ProductOptions[];
  featuresData: ModalPrinterComparisonFeatures;
  printersPriority?: string[];
  onAnalytics?: (data: ModalPrinterComparisonAnalyticsPayload) => void;
};

export default function ModalPrinterComparisonTable({
  columnsNumber,
  initialSelection,
  fullSpecsLabel,
  productsInfo,
  productsOptions,
  featuresData,
  printersPriority,
  onAnalytics,
}: Readonly<ModalPrinterComparisonTableProps>) {
  const { featuresKeys, printersFeaturesList } = featuresData;
  const printersMapArray = Array.from({ length: columnsNumber });
  const shouldRenderSelect = productsInfo.length > columnsNumber;

  const [selectedArray, setSelectedArray] = useState(initialSelection);

  const [optionsArray, setOptionsArray] = useState(
    calculateOptions(productsOptions, selectedArray)
  );

  const somePrinterHasSpecs = productsInfo
    .filter((product) => selectedArray.includes(product.id))
    .some((product) => product.specs.url);

  const commonFeatures = getCommonFeatures(
    selectedArray,
    printersFeaturesList,
    featuresKeys
  );

  useEffect(() => {
    const newArray = selectedArray.slice(0, 2);
    if (columnsNumber === MAX_SCREEN_ELEMENTS) {
      newArray.push(
        getNextColumn(newArray, productsOptions, printersPriority, 0)
      );
    }
    setSelectedArray(newArray);
    const newOptionsArray = calculateOptions(productsOptions, newArray);
    setOptionsArray(newOptionsArray);
  }, [columnsNumber]);

  const onChange = (selectedOption: SelectedOption, column: number) => {
    const newSelectedArray = [...selectedArray];
    if (selectedOption.value !== undefined) {
      newSelectedArray[column] = selectedOption.value;
    } else {
      newSelectedArray[column] = '';
    }
    setSelectedArray(newSelectedArray);
    setOptionsArray(calculateOptions(productsOptions, newSelectedArray));
  };

  if (selectedArray.length !== columnsNumber) return null;

  return (
    <ContainerRows>
      {printersMapArray.map((_, printerColumn) => {
        const printer = getPrinterInfoById(
          selectedArray[printerColumn],
          productsInfo
        );
        const selectedPrinter = selectedArray[printerColumn];
        const featuresForColumn = getPrinterFeaturesById(
          selectedArray[printerColumn],
          printersFeaturesList
        );
        return (
          <ContainerColumn key={`${selectedPrinter}-column-${printerColumn}`}>
            <Header>
              {shouldRenderSelect && (
                <PrinterSelect
                  printerColumn={printerColumn}
                  options={optionsArray[printerColumn]}
                  selectedArray={selectedArray}
                  onChange={onChange}
                />
              )}
              {selectedPrinter && <PrinterImage printer={printer} />}
            </Header>
            {printer && (
              <>
                {somePrinterHasSpecs && (
                  <PrinterSpec
                    printer={printer}
                    fullSpecsLabel={fullSpecsLabel}
                    onAnalytics={onAnalytics}
                  />
                )}
                {commonFeatures.map((key: string) => (
                  <Feature key={key}>
                    <PrinterFeature
                      features={featuresForColumn}
                      featureKey={key}
                    />
                  </Feature>
                ))}
              </>
            )}
          </ContainerColumn>
        );
      })}
    </ContainerRows>
  );
}
